/**
 * Chart Properties dialog — visually a sibling of FibLevelsDialog
 * (same backdrop, header, footer with Cancel/Save, same shared
 * ColorPicker widget).
 *
 * Edit model: changes apply to the chart LIVE so the user sees
 * what they're doing in real-time. On open we snapshot the theme
 * to a rollback ref; Save just closes (theme already applied),
 * Cancel / Esc / backdrop-click / × restore the snapshot.
 *
 * Fields:
 *   - Preset (Red/Green | Mono Green/Black) — replaces the whole theme
 *   - Show grid checkbox
 *   - 8 color pickers (background, candle bodies, candle borders,
 *     pending order, SL, TP)
 *
 * Persistence is via the workspace payload (ui.prefs.theme); the
 * existing workspace auto-save picks up the live mutations on its
 * next debounce window after Save.
 */
import { useEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
} from "@/state/hooks";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { formatColor, parseColor } from "@/utils/colorWithAlpha";
import { selectChartChrome } from "@/state/selectors/effectiveTheme";
import {
  LineStyleDialog,
  type LineStyleConfig,
  type LineStyleName,
} from "@/components/LineStyleDialog/LineStyleDialog";
import { type MoreAction } from "@/components/MoreActionsMenu/MoreActionsMenu";
import { SettingsPanel } from "@/tools/_shared/SettingsPanel";
import type { ScopeLevel } from "@/tools/_shared/registry";
import {
  CHART_SCHEMES,
  setChartColorsDialogOpen,
  setTabTheme,
  setTabThemeCache,
  type ChartScheme,
  type ChartTheme,
} from "@/state/slices/uiSlice";
import {
  saveUserTheme,
  setUserTheme,
  setUserThemeCache,
} from "@/state/slices/userPrefsSlice";
import styles from "./ChartColorsDialog.module.css";

// Only the static color-only fields live in this inline list.
// Pending / SL / TP / live-price all get their own LineStyleDialog
// rows below (color + width + style edited together).
type ColorKey =
  | "bg"
  | "bullBody"
  | "bearBody"
  | "bullBorderWick"
  | "bearBorderWick";

interface ColorField {
  key: ColorKey;
  label: string;
}

const FIELDS: ColorField[] = [
  { key: "bg", label: "Background" },
  { key: "bullBody", label: "Bull candle (body)" },
  { key: "bearBody", label: "Bear candle (body)" },
  { key: "bullBorderWick", label: "Bull border + wick" },
  { key: "bearBorderWick", label: "Bear border + wick" },
];

// Lines that get a dedicated LineStyleDialog editor (color +
// thickness + style). Each entry knows how to read its triplet
// from the theme and how to write a patch back.
interface LineSpec {
  id: "price" | "pending" | "sl" | "tp" | "posLong" | "posShort";
  label: string;
  colorKey: keyof ChartTheme;
  widthKey: keyof ChartTheme;
  styleKey: keyof ChartTheme;
  /** Shown when the saved theme predates this line's fields (the
   *  triplet is optional on ChartTheme). */
  fallback?: { color: string; width: number; style: LineStyleName };
}

const LINES: LineSpec[] = [
  {
    id: "price",
    label: "Live price line",
    colorKey: "priceLineColor",
    widthKey: "priceLineWidth",
    styleKey: "priceLineStyle",
  },
  {
    id: "pending",
    label: "Pending order",
    colorKey: "pendingOrder",
    widthKey: "pendingOrderWidth",
    styleKey: "pendingOrderStyle",
  },
  {
    id: "sl",
    label: "Stop-loss",
    colorKey: "slColor",
    widthKey: "slWidth",
    styleKey: "slStyle",
  },
  {
    id: "tp",
    label: "Take-profit",
    colorKey: "tpColor",
    widthKey: "tpWidth",
    styleKey: "tpStyle",
  },
  {
    id: "posLong",
    label: "Running buy (entry)",
    colorKey: "posLongColor",
    widthKey: "posLongWidth",
    styleKey: "posLongStyle",
    fallback: { color: "#26a69a", width: 2, style: "solid" },
  },
  {
    id: "posShort",
    label: "Running sell (entry)",
    colorKey: "posShortColor",
    widthKey: "posShortWidth",
    styleKey: "posShortStyle",
    fallback: { color: "#ef5350", width: 2, style: "solid" },
  },
];

const LINE_STYLE_DASHARRAY: Record<string, string> = {
  solid: "",
  dotted: "2 3",
  dashed: "6 3",
  largeDashed: "10 4",
  sparseDotted: "1 5",
};

// Which sub-line is being edited in the LineStyleDialog. Local
// (not Redux) — purely a UI nav concern. Extend with 'sl' | 'tp'
// when those get tweakable width/style.
type EditingLine = null | LineSpec["id"];

const SCHEME_LABELS: Record<ChartScheme, string> = {
  redGreen: "Red / Green",
  monoBlack: "Mono Green / Black",
};

// Two scopes: "Workspace" (per-tab override — saved with the
// workspace, so each chart can have its own look) and "User
// defaults" (server-persisted user-wide fallback).
type Scope = "workspace" | "user";

const SCOPE_LABELS: Record<Scope, string> = {
  workspace: "Workspace",
  user: "User defaults",
};

export function ChartColorsDialog() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.chartColorsDialogOpen);

  // The chart the user right-clicked = the focused active tab.
  // If they switch tabs while the dialog is open, "Workspace"
  // scope follows along (each chart has its own override).
  const activeTabId = useActiveTabId();
  const activeSymbol = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.symbol ?? "",
  );
  // Current app UI mode. Chart properties are stored separately per
  // mode (a user can style dark and light independently), so the dialog
  // reads + writes the slot matching whatever theme is active right now.
  const appTheme = useAppSelector((s) => s.appearance.theme);
  const isLight = appTheme === "light";
  const tabThemeOverride = useAppSelector(
    (s) =>
      (isLight
        ? s.ui.byTab[activeTabId]?.themeLight
        : s.ui.byTab[activeTabId]?.theme) ?? null,
  );
  const tabThemeCache = useAppSelector(
    (s) =>
      (isLight
        ? s.ui.byTab[activeTabId]?.themeCacheLight
        : s.ui.byTab[activeTabId]?.themeCache) ?? {},
  );
  const userTheme = useAppSelector((s) =>
    isLight ? s.userPrefs.themeLight : s.userPrefs.theme,
  );
  const userThemeCache = useAppSelector((s) =>
    isLight ? s.userPrefs.themeCacheLight : s.userPrefs.themeCache,
  );
  // Both modes' user-scope state — needed to persist BOTH slots on Save
  // and to snapshot/restore the full user theme on Cancel.
  const userThemeDark = useAppSelector((s) => s.userPrefs.theme);
  const userThemeLight = useAppSelector((s) => s.userPrefs.themeLight);
  const userCacheDark = useAppSelector((s) => s.userPrefs.themeCache);
  const userCacheLight = useAppSelector((s) => s.userPrefs.themeCacheLight);
  const userSaving = useAppSelector((s) => s.userPrefs.saving);

  // Scope selector — defaults to "Workspace" (per-tab override).
  // Resets to workspace on every fresh open below.
  const [scope, setScope] = useState<Scope>("workspace");

  // The theme currently being EDITED.
  //   - workspace scope with override → that tab's theme
  //   - workspace scope without override → undefined (inherit UI)
  //   - user scope with override → user theme
  //   - user scope without override → undefined (inherit UI)
  const editingTheme: ChartTheme | undefined =
    scope === "workspace"
      ? tabThemeOverride ?? undefined
      : userTheme ?? undefined;

  // Active-mode slot writers — wrap the mode-keyed reducers so the
  // helper functions below stay terse and always target the slot for
  // whatever app theme is active.
  const putActiveTabTheme = (theme: ChartTheme | null) =>
    dispatch(setTabTheme({ tabId: activeTabId, mode: appTheme, theme }));
  const putActiveTabCache = (
    themeCache: Partial<Record<ChartScheme, ChartTheme>>,
  ) =>
    dispatch(setTabThemeCache({ tabId: activeTabId, mode: appTheme, themeCache }));
  const putActiveUserTheme = (theme: ChartTheme | null) =>
    dispatch(setUserTheme({ mode: appTheme, theme }));
  const putActiveUserCache = (
    themeCache: Partial<Record<ChartScheme, ChartTheme>>,
  ) => dispatch(setUserThemeCache({ mode: appTheme, themeCache }));

  // Rollback snapshot — captured once on each open. Captures BOTH
  // scopes AND both app-theme slots (dark + light) so Cancel fully
  // restores whatever the user touched, in either mode.
  const rollbackRef = useRef<{
    tabThemes: Record<
      string,
      { dark: ChartTheme | null; light: ChartTheme | null }
    >;
    tabCaches: Record<
      string,
      {
        dark: Partial<Record<ChartScheme, ChartTheme>>;
        light: Partial<Record<ChartScheme, ChartTheme>>;
      }
    >;
    userThemeDark: ChartTheme | null;
    userThemeLight: ChartTheme | null;
    userCacheDark: Partial<Record<ChartScheme, ChartTheme>>;
    userCacheLight: Partial<Record<ChartScheme, ChartTheme>>;
  } | null>(null);

  // Which sub-popup is open. The LineStyleDialog is always
  // mounted (cheap when closed); we just toggle its `open` prop.
  const [editingLine, setEditingLine] = useState<EditingLine>(null);

  // Snapshot on transition closed→open. Captures BOTH scopes.
  const prevOpenRef = useRef(false);
  // Need a fresh selector read inside the effect to snapshot tab
  // overrides — captured separately so cancel can restore them.
  const allByTab = useAppSelector((s) => s.ui.byTab);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Reset scope to "Workspace" on every open per design.
      setScope("workspace");
      // Snapshot every tab's theme + cache for BOTH modes so Cancel can
      // restore any of them (the dialog can follow tab switches mid-edit,
      // and the user can flip the app theme while it's open).
      const tabThemes: Record<
        string,
        { dark: ChartTheme | null; light: ChartTheme | null }
      > = {};
      const tabCaches: Record<
        string,
        {
          dark: Partial<Record<ChartScheme, ChartTheme>>;
          light: Partial<Record<ChartScheme, ChartTheme>>;
        }
      > = {};
      for (const [tid, tabState] of Object.entries(allByTab)) {
        tabThemes[tid] = {
          dark: tabState.theme ?? null,
          light: tabState.themeLight ?? null,
        };
        tabCaches[tid] = {
          dark: { ...(tabState.themeCache ?? {}) },
          light: { ...(tabState.themeCacheLight ?? {}) },
        };
      }
      rollbackRef.current = {
        tabThemes,
        tabCaches,
        userThemeDark: userThemeDark ? { ...userThemeDark } : null,
        userThemeLight: userThemeLight ? { ...userThemeLight } : null,
        userCacheDark: { ...userCacheDark },
        userCacheLight: { ...userCacheLight },
      };
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const closeAndDiscard = () => {
    const r = rollbackRef.current;
    if (r) {
      // Restore every tab's theme + per-scheme cache, BOTH modes.
      for (const [tid, themes] of Object.entries(r.tabThemes)) {
        dispatch(setTabTheme({ tabId: tid, mode: "dark", theme: themes.dark }));
        dispatch(setTabTheme({ tabId: tid, mode: "light", theme: themes.light }));
        const caches = r.tabCaches[tid] ?? { dark: {}, light: {} };
        dispatch(
          setTabThemeCache({ tabId: tid, mode: "dark", themeCache: caches.dark }),
        );
        dispatch(
          setTabThemeCache({
            tabId: tid,
            mode: "light",
            themeCache: caches.light,
          }),
        );
      }
      // Restore user-scope LOCAL state, both modes. The server-side save
      // (if the user explicitly clicked Save during the session) already
      // committed and isn't rolled back — that's "stop editing" semantics.
      dispatch(setUserTheme({ mode: "dark", theme: r.userThemeDark }));
      dispatch(setUserTheme({ mode: "light", theme: r.userThemeLight }));
      dispatch(setUserThemeCache({ mode: "dark", themeCache: r.userCacheDark }));
      dispatch(
        setUserThemeCache({ mode: "light", themeCache: r.userCacheLight }),
      );
    }
    dispatch(setChartColorsDialogOpen(false));
  };

  const closeAndKeep = async () => {
    // User scope persists to the SERVER. Workspace scope (per-
    // tab) goes through the workspace autosave. Always PUT the
    // current user state on Save — both mode slots, so a tweak in
    // either light or dark is committed regardless of which mode is
    // active at close.
    try {
      await dispatch(
        saveUserTheme({
          theme: userThemeDark,
          themeCache: userCacheDark,
          themeLight: userThemeLight,
          themeCacheLight: userCacheLight,
        }),
      ).unwrap();
    } catch {
      // Network blip — leave local state as-is; next session's
      // fetchUserTheme will resync.
    }
    dispatch(setChartColorsDialogOpen(false));
  };

  /** Next scope down — what the current scope inherits from.
   *  Workspace (per-tab) inherits from User; User inherits from
   *  built-in defaults. */
  const inheritFromLabel: string =
    scope === "workspace"
      ? userTheme
        ? "User defaults"
        : "Built-in defaults"
      : "Built-in defaults";

  /** Seed theme for Customize / Copy-from at the current scope. */
  const inheritedTheme = (s: Scope): ChartTheme => {
    if (s === "workspace")
      return userTheme ?? { ...CHART_SCHEMES.redGreen };
    return { ...CHART_SCHEMES.redGreen };
  };

  const applyPreset = (newScheme: ChartScheme) => {
    if (scope === "workspace") {
      // Per-tab themeCache: snapshot the OLD scheme, load the
      // NEW scheme from cache (or defaults). Clone before write
      // to dodge Immer's read-only freeze on Redux state.
      if (tabThemeOverride) {
        putActiveTabCache({
          ...tabThemeCache,
          [tabThemeOverride.scheme]: { ...tabThemeOverride },
        });
      }
      const next: ChartTheme = {
        ...(tabThemeCache[newScheme] ?? CHART_SCHEMES[newScheme]),
        scheme: newScheme,
      };
      putActiveTabTheme(next);
    } else {
      // User scope — same pattern with user-scope cache.
      if (userTheme) {
        putActiveUserCache({
          ...userThemeCache,
          [userTheme.scheme]: { ...userTheme },
        });
      }
      const next: ChartTheme = {
        ...(userThemeCache[newScheme] ?? CHART_SCHEMES[newScheme]),
        scheme: newScheme,
      };
      putActiveUserTheme(next);
    }
  };

  const patchLive = (patch: Partial<ChartTheme>) => {
    if (scope === "workspace") {
      if (!tabThemeOverride) return;
      putActiveTabTheme({ ...tabThemeOverride, ...patch });
    } else {
      if (!userTheme) return;
      putActiveUserTheme({ ...userTheme, ...patch });
    }
  };

  const resetToDefaults = () => {
    if (scope === "workspace") {
      const baseScheme = tabThemeOverride?.scheme ?? "redGreen";
      putActiveTabTheme({ ...CHART_SCHEMES[baseScheme] });
    } else {
      const baseScheme = userTheme?.scheme ?? "redGreen";
      putActiveUserTheme({ ...CHART_SCHEMES[baseScheme] });
    }
  };

  /** Customize this scope by seeding it with the currently-
   *  inherited theme. */
  const startOverride = () => {
    const seed = { ...inheritedTheme(scope) };
    if (scope === "workspace") putActiveTabTheme(seed);
    else putActiveUserTheme(seed);
  };

  /** Drop the current scope's override → falls back to inheritance. */
  const removeOverride = () => {
    if (scope === "workspace") putActiveTabTheme(null);
    else putActiveUserTheme(null);
  };

  /** Copy from the inherited source. Workspace pulls from User;
   *  User has no source. */
  const copyFromInherited = () => {
    if (scope !== "workspace") return;
    putActiveTabTheme({ ...inheritedTheme(scope) });
  };

  // Footer "⋯" actions — handed to the shared panel.
  const cpActions: MoreAction[] = [];
  if (editingTheme) {
    cpActions.push({
      label: "Reset to defaults",
      onClick: resetToDefaults,
      title:
        "Reset this scope to the active scheme's preset defaults (does not save; Cancel still reverts)",
    });
  }
  if (editingTheme && scope === "workspace" && userTheme) {
    cpActions.push({
      label: `Copy from ${inheritFromLabel}`,
      onClick: copyFromInherited,
      title: `Replace this chart's theme with ${inheritFromLabel}'s current value`,
    });
  }
  if (editingTheme) {
    cpActions.push({
      label: "Remove override",
      onClick: removeOverride,
      title: "Remove the override and inherit from the next scope down",
    });
  }

  return (
    <SettingsPanel
      open={open}
      // Properties are stored separately per app mode — show which one.
      title={`Chart Properties · ${isLight ? "Light" : "Dark"} mode`}
      scope={{
        options: [
          {
            value: "workspace",
            label: `Workspace${activeSymbol ? ` (${activeSymbol})` : ""}`,
          },
          { value: "user", label: SCOPE_LABELS.user },
        ],
        current: scope,
        onChange: (s: ScopeLevel) => setScope(s as Scope),
      }}
      actions={cpActions}
      saving={userSaving}
      onCancel={closeAndDiscard}
      onSave={closeAndKeep}
      bodyClassName={styles.roomy}
    >
      {/* Chart-scope with no override yet → show the inherit banner
          instead of the full editor; Customize materialises an override. */}
      {!editingTheme && (
            <div className={styles.inheritBanner}>
              <p className={styles.inheritText}>
                <strong>
                  {scope === "workspace"
                    ? activeSymbol
                      ? `This chart (${activeSymbol})`
                      : "This chart"
                    : SCOPE_LABELS[scope]}
                </strong>{" "}
                is inheriting from{" "}
                <strong>{inheritFromLabel}</strong>.
              </p>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={startOverride}
              >
                Customize
                {scope === "workspace" ? " this chart" : ""}
              </button>
            </div>
          )}

        {editingTheme ? (
          <div className={styles.bodyStack}>
          {/* Presets — clicking either rewrites the live theme. */}
          <div>
            <p className={styles.sectionLabel}>Preset</p>
            <div className={styles.presetRow}>
              {(Object.keys(SCHEME_LABELS) as ChartScheme[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.presetBtn} ${
                    editingTheme!.scheme === s ? styles.presetActive : ""
                  }`}
                  onClick={() => applyPreset(s)}
                >
                  {SCHEME_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Show-grid toggle. Bool, not a color, but lives here
              because it's part of the chart's background chrome. */}
          <div className={styles.row}>
            <label className={styles.rowLabel} htmlFor="cprop-grid">
              Show grid
            </label>
            <input
              id="cprop-grid"
              type="checkbox"
              className={styles.check}
              checked={editingTheme!.showGrid}
              onChange={(e) =>
                patchLive({ showGrid: e.target.checked })
              }
            />
          </div>

          {/* Per-color rows using the shared ColorPicker. Each
              field's stored string carries both the hex AND the
              opacity (encoded as rgba when alpha < 1) so the
              picker's opacity slider feeds through to the chart. */}
          {FIELDS.map((f) => {
            // The chart whitens a still-default-dark background in light mode
            // (selectChartChrome). Show that EFFECTIVE colour for the
            // Background swatch so the dialog matches the actual chart instead
            // of the stored dark sentinel; all other fields render as stored.
            const shown =
              f.key === "bg"
                ? selectChartChrome(
                    editingTheme![f.key] as string,
                    appTheme,
                    true,
                  ).bg
                : (editingTheme![f.key] as string);
            const { color, opacity } = parseColor(shown);
            return (
              <div key={f.key} className={styles.row}>
                <span className={styles.rowLabel}>{f.label}</span>
                <ColorPicker
                  value={color}
                  opacity={opacity}
                  onChange={(c, o) =>
                    patchLive({
                      [f.key]: formatColor(c, o),
                    } as Partial<ChartTheme>)
                  }
                  ariaLabel={`Color for ${f.label}`}
                />
              </div>
            );
          })}

          {/* One row per editable line. Each opens the shared
              LineStyleDialog with its color/width/style triplet. */}
          {LINES.map((line) => {
            const color =
              (editingTheme![line.colorKey] as string) ??
              line.fallback?.color ??
              "#787b86";
            const width =
              (editingTheme![line.widthKey] as number) ??
              line.fallback?.width ??
              1;
            const style =
              (editingTheme![line.styleKey] as LineStyleName) ??
              line.fallback?.style ??
              "solid";
            return (
              <div key={line.id} className={styles.row}>
                <span className={styles.rowLabel}>{line.label}</span>
                <button
                  type="button"
                  className={styles.lineBtn}
                  onClick={() => setEditingLine(line.id)}
                >
                  <svg
                    className={styles.linePreview}
                    viewBox="0 0 56 12"
                    preserveAspectRatio="none"
                    aria-hidden
                  >
                    <line
                      x1="2"
                      y1="6"
                      x2="54"
                      y2="6"
                      stroke={color}
                      strokeWidth={width}
                      strokeDasharray={
                        LINE_STYLE_DASHARRAY[style] ?? ""
                      }
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>Edit…</span>
                </button>
              </div>
            );
          })}

        {/* Sub-popup for the focused line editor. One instance
            switches between the four lines based on `editingLine`.
            Live edits dispatch immediately; the sub-dialog's own
            Cancel restores just that line's pre-open state. The
            parent's Cancel still rolls back EVERYTHING via
            rollbackRef. */}
        {(() => {
          if (!editingLine) {
            return (
              <LineStyleDialog
                open={false}
                title=""
                value={{ color: "#000000", width: 1, style: "solid" }}
                onChange={() => undefined}
                onClose={() => setEditingLine(null)}
              />
            );
          }
          const line = LINES.find((l) => l.id === editingLine)!;
          return (
            <LineStyleDialog
              open
              title={line.label}
              value={{
                color:
                  (editingTheme![line.colorKey] as string) ??
                  line.fallback?.color ??
                  "#787b86",
                width:
                  (editingTheme![line.widthKey] as number) ??
                  line.fallback?.width ??
                  1,
                style:
                  (editingTheme![line.styleKey] as LineStyleName) ??
                  line.fallback?.style ??
                  "solid",
              }}
              onChange={(next: LineStyleConfig) =>
                patchLive({
                  [line.colorKey]: next.color,
                  [line.widthKey]: next.width,
                  [line.styleKey]:
                    next.style as ChartTheme["priceLineStyle"],
                } as Partial<ChartTheme>)
              }
              onClose={() => setEditingLine(null)}
            />
          );
        })()}
          </div>
        ) : null}
    </SettingsPanel>
  );
}
